export const TITLE_MAX = 200;
export const CONTENT_MAX = 50000;
export const TAGS_MAX = 10;
export const TAG_LENGTH_MAX = 50;

export const CATEGORIES = [
  'Academic',
  'Athletic',
  'Arts',
  'Community Service',
  'Leadership',
  'Work Experience',
  'Internship',
  'Research',
  'Personal Growth',
  'Other',
];

export const CATEGORY_COLORS = {
  'Academic': '#3b82f6',
  'Athletic': '#f97316',
  'Arts': '#a855f7',
  'Community Service': '#22c55e',
  'Leadership': '#eab308',
  'Work Experience': '#6b7280',
  'Internship': '#06b6d4',
  'Research': '#8b5cf6',
  'Personal Growth': '#ec4899',
  'Other': '#94a3b8',
};

export const ACTIVITY_TYPES = [
  'Academic',
  'Athletic',
  'Arts',
  'Community Service',
  'Leadership',
  'Work Experience',
  'Internship',
  'Research',
  'Personal Growth',
  'Other',
];

export const AWARD_LEVELS = [
  'School',
  'Regional',
  'State',
  'National',
  'International',
];

export function formatDate(dateStr) {
  if (!dateStr) return '';
  let d;
  // If the string is a plain YYYY-MM-DD date (no time component), parse as local time
  // to avoid UTC midnight shifting back a day in US timezones.
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(dateStr);
  }
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function todayString() {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

// Minimum age required to use BragSheet. Used by both the signup form
// and the backend record-signup handler so they stay in sync.
export const MIN_SIGNUP_AGE = 13;

/**
 * Returns true if a person born on `dob` is at least `MIN_SIGNUP_AGE` years old
 * as of `asOf` (defaults to today). `dob` and `asOf` are either Date objects
 * or 'YYYY-MM-DD' strings. Returns false for any invalid input.
 *
 * Boundary semantics:
 *  - Exact 13th birthday (dob + 13y === asOf): allowed.
 *  - One day before 13th birthday: rejected.
 *  - Future dates / NaN / malformed: rejected.
 */
export function isAtLeast13(dob, asOf = new Date()) {
  const dobDate = parseYmd(dob);
  if (!dobDate) return false;
  const asOfDate = parseYmd(asOf) || (asOf instanceof Date && !isNaN(asOf.getTime()) ? asOf : null);
  if (!asOfDate) return false;
  // Reject future DOBs outright
  if (dobDate.getTime() > asOfDate.getTime()) return false;

  let age = asOfDate.getFullYear() - dobDate.getFullYear();
  const monthDiff = asOfDate.getMonth() - dobDate.getMonth();
  const dayDiff = asOfDate.getDate() - dobDate.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }
  return age >= MIN_SIGNUP_AGE;
}

function parseYmd(input) {
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }
  if (typeof input !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  // Reject e.g. 2024-02-30 which JS would normalize to March
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

// Converts Cognito error names to human-readable messages for display in auth forms
export function mapCognitoError(err) {
  const name = err && (err.name || err.code || '');
  switch (name) {
    case 'UsernameExistsException':
      return 'An account with this email already exists.';
    case 'InvalidPasswordException':
      return 'Password must be at least 8 characters.';
    case 'InvalidParameterException':
      return 'Please check your information and try again.';
    case 'NotAuthorizedException':
      return 'Invalid email or password.';
    case 'UserNotFoundException':
      return 'Invalid email or password.';
    case 'UserNotConfirmedException':
      return 'Please verify your email address before signing in.';
    default:
      return err && err.message ? err.message : 'An error occurred. Please try again.';
  }
}

export function getPasswordStrength(password) {
  if (!password) return { score: 0, level: 'weak', label: '', suggestions: [] };

  const checks = [
    { test: password.length >= 8, suggestion: 'Use at least 8 characters' },
    { test: /[a-z]/.test(password), suggestion: 'Add a lowercase letter' },
    { test: /[A-Z]/.test(password), suggestion: 'Add an uppercase letter' },
    { test: /[0-9]/.test(password), suggestion: 'Add a number' },
    { test: /[^a-zA-Z0-9]/.test(password), suggestion: 'Add a special character' },
    { test: password.length >= 12, suggestion: 'Use 12+ characters for extra security' },
  ];

  const score = checks.filter(c => c.test).length;
  const suggestions = checks.filter(c => !c.test).map(c => c.suggestion);

  let level, label;
  if (score <= 2) { level = 'weak'; label = 'Weak'; }
  else if (score === 3) { level = 'fair'; label = 'Fair'; }
  else if (score === 4) { level = 'good'; label = 'Good'; }
  else { level = 'strong'; label = 'Strong'; }

  return { score, level, label, suggestions };
}
